import { Card, Flex, Skeleton, Spin, Typography } from "antd"
import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import React, { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../../hooks/use-app-selector.ts"
import { cn } from "../../../../../../../utils/cn.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import {
  loadFinancialAlerts,
  loadFinancialSnapshot
} from "../../../../../redux/project-finance/project-finance.thunks.ts"
import { FETCHING_SNAPSHOT_ENUM } from "../../../../../redux/project.types.ts"
import type {
  FinancialTransactionSnapshot,
  FinancialTransactionSnapshotKeys
} from "../../../../../types/project-finance.types.tsx"
import { ContractFigure } from "./ContractFigure.tsx"
import { type FinancialTableRow, getColumns } from "./columns.tsx"

const { Title } = Typography

const SEPARATOR = "__separator__" as const

const LABELS: Record<
  FinancialTransactionSnapshotKeys,
  { nameKey: string; isHighlight?: boolean }
> = {
  sales_price: {
    nameKey: "financeSnapshot.rows.salesPrice",
    isHighlight: false
  },
  sales_price_main: { nameKey: "financeSnapshot.rows.salesPriceMain" },
  sales_price_referral: { nameKey: "financeSnapshot.rows.salesPriceReferral" },
  sales_price_other: { nameKey: "financeSnapshot.rows.salesPriceOther" },
  total_expenses: { nameKey: "financeSnapshot.rows.totalExpenses" },
  project_margin: {
    nameKey: "financeSnapshot.rows.projectMargin",
    isHighlight: false
  },
  lead_cost: { nameKey: "financeSnapshot.rows.leadCost" },
  project_net_margin: { nameKey: "financeSnapshot.rows.projectNetMargin" },
  office_project_income: {
    nameKey: "financeSnapshot.rows.officeProjectIncome"
  },
  office_total_revenues: {
    nameKey: "financeSnapshot.rows.officeTotalRevenues"
  },
  total_sales_commission: {
    nameKey: "financeSnapshot.rows.totalSalesCommission",
    isHighlight: false
  },
  reimbursable: { nameKey: "financeSnapshot.rows.reimbursable" }
}

const ROW_ORDER: (
  | { key: FinancialTransactionSnapshotKeys }
  | typeof SEPARATOR
)[] = [
  { key: "sales_price" },
  { key: "sales_price_main" },
  { key: "sales_price_referral" },
  { key: "sales_price_other" },
  { key: "total_expenses" },
  { key: "reimbursable" },
  SEPARATOR,
  { key: "project_margin" },
  { key: "lead_cost" },
  { key: "project_net_margin" },
  { key: "office_project_income" },
  { key: "office_total_revenues" },
  { key: "total_sales_commission" }
]

interface ProjectViewFinanceSnapshotProps {
  showSalesSnapshot: boolean
  showCommissionSnapshot: boolean
}

// The backend suppresses a side's mismatch rule once its exceeded rule fires.
const ALERT_TARGET: Record<string, "sales_price" | "total_expenses"> = {
  receivables_mismatch: "sales_price",
  contract_value_exceeded: "sales_price",
  payables_mismatch: "total_expenses",
  contract_expenses_exceeded: "total_expenses"
}

export function ProjectViewFinanceSnapshot({
  showSalesSnapshot,
  showCommissionSnapshot
}: ProjectViewFinanceSnapshotProps) {
  const projectId = useParams()?.id as string
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const data = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot
  )

  const snapShotFetching = useAppSelector(
    (state) =>
      state?.project?.fetchingSnapshotMap?.[FETCHING_SNAPSHOT_ENUM.FINANCIAL]
  )

  const projectDetails = useAppSelector((state) => state?.project?.project)
  const alerts = useAppSelector((state) => state?.project?.financialAlerts)
  const projectDetailsFetching = useAppSelector(
    (state) => state?.project?.fetching
  )

  const fetching = snapShotFetching || projectDetailsFetching

  const snapshotColumns = useMemo(() => getColumns(t), [t])

  const handleFetchFinancialSnapshot = useCallback(() => {
    dispatch(loadFinancialSnapshot(projectId))
  }, [dispatch, projectId])

  const alertByRow = useMemo(() => {
    const rows: Partial<
      Record<
        "sales_price" | "total_expenses",
        { severity: "warning" | "critical"; message: string }
      >
    > = {}
    const copy: Record<string, string> = {
      receivables_mismatch: t("financeSnapshot.receivablesMismatch"),
      contract_value_exceeded: t("financeSnapshot.contractValueExceeded"),
      payables_mismatch: t("financeSnapshot.payablesMismatch"),
      contract_expenses_exceeded: t("financeSnapshot.contractExpensesExceeded")
    }

    for (const alert of alerts?.alerts ?? []) {
      // The actual basis mismatches on nearly every project until something is paid.
      if (alert.basis !== "planned") continue
      const key = ALERT_TARGET[alert.rule]
      if (!key) continue
      const severity = alert.severity === "critical" ? "critical" : "warning"
      if (rows[key]?.severity === "critical") continue
      rows[key] = { severity, message: copy[alert.rule] }
    }

    return rows
  }, [alerts, t])

  const financialTableRows = useMemo((): FinancialTableRow[] => {
    if (!data) return []

    const rows: FinancialTableRow[] = []
    let separatorIndex = 0
    let afterSeparator = false
    let afterProjectMargin = false

    for (const entry of ROW_ORDER) {
      if (entry === SEPARATOR) {
        afterSeparator = true
        if (showSalesSnapshot && showCommissionSnapshot) {
          rows.push({
            key: `separator-${separatorIndex++}`,
            name: "",
            planned: 0,
            actual: 0,
            exercised: 0,
            exercised_vs_plan: { value: null, color: null },
            isSeparator: true
          })
        }
        continue
      }

      if (!afterSeparator && !showSalesSnapshot) continue
      if (afterSeparator && !showCommissionSnapshot) continue

      const item = data[entry.key as keyof FinancialTransactionSnapshot]

      if (
        item &&
        typeof item === "object" &&
        "planned" in item &&
        "actual" in item &&
        "exercised" in item &&
        "exercised_vs_plan" in item
      ) {
        const config = LABELS[entry.key]

        rows.push({
          key: entry.key,
          name: config ? t(config.nameKey) : entry.key,
          planned: item.planned,
          actual: item.actual,
          exercised: item.exercised,
          exercised_vs_plan: item.exercised_vs_plan,
          isHighlight: config?.isHighlight,
          hidePercent: afterProjectMargin,
          ...(entry.key === "sales_price" && {
            tooltip: projectDetails?.sales_price
              ? t("financeSnapshot.salesPriceTooltip", {
                  amount: budgetFormat(projectDetails.sales_price)
                })
              : t("financeSnapshot.noContractValue")
          }),
          ...(entry.key === "total_expenses" && {
            tooltip: projectDetails?.planned_payable
              ? t("financeSnapshot.totalExpensesTooltip", {
                  amount: budgetFormat(projectDetails.planned_payable)
                })
              : t("financeSnapshot.noPlannedExpenses")
          })
        })
      }

      if (entry.key === "project_margin") afterProjectMargin = true
    }

    return rows
  }, [data, showSalesSnapshot, showCommissionSnapshot, projectDetails, t])

  useEffect(() => {
    handleFetchFinancialSnapshot()
  }, [handleFetchFinancialSnapshot])

  // The card renders in the sidebar, which outlives the Finance route that
  // used to be the only thing fetching these. Without this the contract
  // warnings are simply absent everywhere except the Finance tab.
  useEffect(() => {
    if (projectId) void dispatch(loadFinancialAlerts(projectId))
  }, [dispatch, projectId])

  // The read may be one the page already started, so the store decides.
  if (fetching && !data) {
    return (
      <Card
        title={<Title level={5}>{t("financeSnapshot.title")}</Title>}
        size="small"
      >
        <Skeleton.Node active className={"w-full h-96"} />
      </Card>
    )
  }

  return (
    <Spin spinning={fetching} size="large">
      <Card
        title={
          <Flex
            align="center"
            justify="space-between"
            gap={12}
            className="py-1"
          >
            <Title level={5} className="!mb-0">
              {t("financeSnapshot.title")}
            </Title>
            <Flex vertical>
              <ContractFigure
                label={t("financeSnapshot.contractValue")}
                value={Number(projectDetails?.sales_price || 0)}
                side={data?.contract_breakdown?.receivable}
                alert={alertByRow.sales_price}
              />
              <ContractFigure
                label={t("financeSnapshot.contractExpenses")}
                value={Number(projectDetails?.planned_payable || 0)}
                side={data?.contract_breakdown?.expenses}
                alert={alertByRow.total_expenses}
              />
            </Flex>
          </Flex>
        }
        size="small"
        className="h-full flex flex-col relative overflow-hidden"
        classNames={{ body: "flex-1 overflow-hidden p-2" }}
      >
        <EntityTable
          surface={TABLE_SURFACES.financialSnapshot}
          tab={DEFAULT_TAB}
          hideConfigButton
          rowKey="key"
          dataSource={financialTableRows}
          columns={snapshotColumns}
          withOutPagination
          pagination={false}
          tableLayout={"fixed"}
          size="small"
          rowClassName={(record) =>
            cn(
              record.isHighlight && "bg-primary/5",
              record.isSeparator &&
                "pointer-events-none bg-[var(--ant-table-header-bg)]"
            )
          }
        />
      </Card>
    </Spin>
  )
}
